import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaQianwenImageParams {
  /**
   * 输入参数
   */
  input?: {
    /**
     * 消息列表，必须包含一个消息对象
     */
    messages: Array<{
      /**
       * 角色，必须为 'user'
       * @values user
       */
      role: 'user';
      /**
       * 内容列表，必须包含一个文本内容对象
       */
      content: Array<{
        /**
         * 文本提示词，描述要生成的图像内容，长度不超过800字符
         * @length 0 - 800
         */
        text: string;
      }>;
    }>;
  };
  /**
   * 可选参数
   */
  parameters?: {
    /**
     * 负面提示词，描述不要生成的图像内容，长度不超过500字符
     * @length 0 - 500
     */
    negative_prompt?: string;
    /**
     * 图像尺寸
     * @default "1328*1328"
     * @values 1664*928 | 1472*1140 | 1328*1328 | 1140*1472 | 928*1664
     */
    size?: '1664*928' | '1472*1140' | '1328*1328' | '1140*1472' | '928*1664';
    /**
     * 生成图像数量，目前只支持生成一张图片
     * @default 1
     * @range 1 - 1
     */
    n?: number;
    /**
     * 随机种子，用于可重现的生成结果
     * @range 0 - 2147483647
     */
    seed?: number;
    /**
     * 是否启用提示词增强
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
  };
}

export interface AlibabaQianwenImageResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_qianwen_image
 *
 * Alibaba Qianwen Image Generator Implementation
 * 
 * Model: qwen-image-plus (固定模型)
 * Features: 文本生成图像功能
 * Image Count: 固定生成1张图片
 * Size: 支持5种分辨率（1664*928、1472*1140、1328*1328、1140*1472、928*1664），默认1328*1328
 * Advanced: 支持负面提示词、提示词增强、水印设置、随机种子控制
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/qwen-image-api
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaQianwenImage } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaQianwenImage({
 *   // 参数见 AlibabaQianwenImageParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaQianwenImageParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaQianwenImage(
  params: AlibabaQianwenImageParams,
): Promise<AlibabaQianwenImageResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_qianwen_image',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaQianwenImageResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
